import {
  createContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from "react";

import { Button, Grid } from "@trussworks/react-uswds";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  V1Clinic,
  SearchFilters,
  V1ClinicPaginationResponse,
  getFiltersFromSearchParams,
  countOptionalSearchFiltersUsed,
  getFiltersWithOptionalCleared,
} from "../../types/v1_schemas";
import BhasoBanner from "../../components/Search/BhasoBanner";
import DesktopControl from "../../components/Search/Filters/Control/DesktopControl";
import { AnalyticsAction, logEvent, logPageView } from "../../utils/analytics";
import MobileControl from "../../components/Search/Filters/Control/MobileControl";
import ZipInput from "../../components/ZipInput";
import DesktopResults from "./DesktopResults";
import MobileResults from "./MobileResults";
import { ReactComponent as Close } from "../../images/close.svg";
import ShareButton from "../../components/ShareButton";
import CompareSelector from "../../components/Compare/CompareSelector";
import ResultsPagination from "../../components/Pagination";
import PrintButton from "../../components/PrintButton";
import InclusionCard from "./InclusionCard";
import getCurrentDatetime from "../../utils/getCurrentDatetime";
import { AxiosResponse } from "axios";
import { QueryApi } from "../../utils/QueryHelper";
import { MILE_DISTANCE_OPTIONS } from "../../utils/filters/distance";
import { handlePageLoad } from "../../utils/accessibility";
import { populateSearchRankLatLng } from "../../utils/map";
import { PaginationConfig } from "../../types/search";
import { isMobile } from "react-device-detect";
import NoResultsLogo from "../../images/OwnPathLogo_stacked 2.svg";

export const SEARCH_FILTER_ORDER = [
  "TypeOfHelp",
  "Distance",
  "Age",
  "Populations",
  "Language",
  "Fees",
  "Hours",
  "Accessibility",
  "Telehealth",
] as const;

const ResponsiveHeader = styled.h1`
  font-size: 1.5rem;
  @media (min-width: 40em) {
    font-size: 2rem;
  }
  @media print {
    display: none !important;
  }
`;

const SearchContainer = styled.div`
  @media screen and (max-width: 45em) {
    flex-basis: 100%;
    order: 1;
  }
  vertical-align: bottom;
`;
const FilterButtonOuter = styled.div`
  background-color: var(--pale_green);
  width: 100%;
`;
const FilterButtonInner = styled.div`
  display: inline-flex;
  padding-left: 65px;
  display: inline-flex;
  padding-top: 5px;
  width: 100%;
`;
const FuncButtonsOuter = styled.div`
  display: flex-inline-block;
  justify-content: flex-end !important;
  width: auto;
  float: right;
  margin-bottom: auto;
  margin-top: auto;
`;

const ZipSearch = styled.form`
  width: fit-content;
  @media screen and (max-width: 40em) {
    width: 100%;
    button {
      width: 30%;
    }
  }
`;

const Ellipses = styled.div`
  display: none;
  @media screen and (max-width: 45em) {
    display: inline-block;
  }
`;

const DivWithLabel = styled.div`
  margin-left: 65px;
  font-family: Comfortaa;
  font-weight: bold;
  font-size: 32px;
  letter-spacing: -3%;
  color: dark_blue !important;
`;

export const CompareContext = createContext<{
  selectedCompareProviders: V1Clinic[];
  setSelectedCompareProviders: Dispatch<SetStateAction<V1Clinic[]>>;
}>({ selectedCompareProviders: [], setSelectedCompareProviders: () => {} });

type PaginationContextProviderProps = {
  paginationConfig: PaginationConfig;
  setPaginationConfig: Dispatch<SetStateAction<PaginationConfig>>;
  didChangePage: boolean;
  setDidChangePage: Dispatch<SetStateAction<boolean>>;
};

export const PaginationContext = createContext<PaginationContextProviderProps>({
  paginationConfig: {
    totalPages: 0,
    currentPage: 1,
    totalItems: 0,
    pageSize: 20,
  },
  setPaginationConfig: () => {},
  didChangePage: false,
  setDidChangePage: () => {},
});

const Search = () => {
  //console.log(">>>>>> IN SEARCH.tsx >>>>>");

  const { t, i18n } = useTranslation();

  // Search filters as URL search params
  const [searchParams, setSearchParams] = useSearchParams();

  // in v1_schemas
  let searchFilters = getFiltersFromSearchParams(searchParams);

  // flag to display change zip input
  const [showZipInput, setShowZipInput] = useState(false);
  // state var to hold zip to ensure changes are only applied after button click
  const [zip, setZip] = useState(searchFilters.distanceSearchZipCode);
  //console.log(`zip is ${zip}`);

  // flag to pass down into Desktop controls to indicate if distance
  // has been updated (by expand radius button when no results)
  const [distanceUpdated, setDistanceUpdated] = useState(false);

  // Pagination State for Search Results
  const [paginationConfig, setPaginationConfig] = useState<PaginationConfig>({
    totalPages: 0,
    currentPage: 1,
    totalItems: 0,
    pageSize: 20,
  });

  const [didChangePage, setDidChangePage] = useState<boolean>(false);

  // added this code specific to API data dt
  const [distanceSearchZipCode, setDistanceSearchZipCode] = useState(
    searchFilters?.distanceSearchZipCode?.length
      ? searchFilters?.distanceSearchZipCode
      : ""
  );

  const [apiResponse, setApiResponse] = useState<V1ClinicPaginationResponse>(
    {} as V1ClinicPaginationResponse
  );

  const responseFn = (response: AxiosResponse) => {
    if (response.status === 200) {
      if (response.data && response.data.data && response.data.data.length) {
        response.data.data = populateSearchRankLatLng(
          response.data.data,
          response.data.pageNumber,
          response.data.pageSize
        );
      }

      setApiResponse(response.data);
    } else {
      console.error(`Provider Search: HTTP error! Status: ${response.status}`);
      setApiResponse({} as V1ClinicPaginationResponse);
    }
  };

  const errorFn = (error: any) => {
    console.error(`Provider Search: Axios Error: ${error.message}`);
    setApiResponse({} as V1ClinicPaginationResponse);
  };

  useEffect(() => {
    searchFilters.language = i18n.language;
    QueryApi(searchFilters, new AbortController().signal, responseFn, errorFn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, i18n.language]);

  // end of added code dt

  const navigate = useNavigate();

  const isWidestRadius = (miles: string) =>
    miles === MILE_DISTANCE_OPTIONS[MILE_DISTANCE_OPTIONS.length - 1];

  const expandSearchRadius = (distanceSearchRadiusInMiles: string) => {
    // if expanded search radius is max, update search filters and stop recursing
    if (isWidestRadius(distanceSearchRadiusInMiles ?? "") && searchFilters) {
      setDistanceUpdated(true);
      setSearchParams(getFiltersWithOptionalCleared({ ...searchFilters, distanceSearchRadiusInMiles }));
      return;
    }

    // otherwise, set radius to next radius and apply updated filters
    const nextRadius =
      MILE_DISTANCE_OPTIONS[
        MILE_DISTANCE_OPTIONS.findIndex(
          (_miles: any) => distanceSearchRadiusInMiles === _miles
        ) + 1
      ];

    const updatedFilters = {
      ...searchFilters,
      distanceSearchRadiusInMiles: nextRadius,
    };

    // Base case -- results found, set search filters & stop recursing
    if (apiResponse.data?.length ?? -1 > 0) {
      setDistanceUpdated(true);
      setSearchParams(getFiltersWithOptionalCleared(updatedFilters));
      return;
    }

    // continue recursing
    expandSearchRadius(nextRadius);
  };

  const runAnalytics = (filters: SearchFilters) => {
    console.log(
      "in performSearch Search and clinicPaginationResponse.data.length is " +
        apiResponse.data.length +
        " " +
        getCurrentDatetime()
    );
    logEvent(AnalyticsAction.ViewSearchResults, {
      count: apiResponse.data ? apiResponse.data.length : 0,
      filter_count: countOptionalSearchFiltersUsed(filters),
      search: filters.distanceSearchZipCode ?? "",
    });
  };

  useEffect(() => {
    // zip is the only required filter - redirect to homepage if it doesn't exist
    if (!distanceSearchZipCode?.length) {
      navigate("/", {
        replace: true,
      });
    } else {
      handlePageLoad({ title: t("searchPageTitle") });

      if (apiResponse && apiResponse.data) {
        runAnalytics(searchFilters ?? []);
      }
      logPageView();
    }

    // eslint-disable-next-line
  }, [searchParams, apiResponse, i18n.language]);

  const [compareProviders, setCompareProviders] = useState<V1Clinic[]>([]);

  return (
    <div className="Search">
      <div>
        <CompareContext.Provider
          value={{
            selectedCompareProviders: compareProviders,
            setSelectedCompareProviders: setCompareProviders,
          }}
        >
          <PaginationContext.Provider
            value={{
              paginationConfig,
              setPaginationConfig,
              didChangePage,
              setDidChangePage,
            }}
          >
            <div className="print-heading">
              <BhasoBanner Visible={true} Bhasos={apiResponse.Bhasos} />
              {!isMobile &&
              <FilterButtonOuter>
                <DivWithLabel>
                  <span className="care-label">{t("exploreCareOptions")}</span>
                </DivWithLabel>
              </FilterButtonOuter>
              }

              <div
                style={{
                  paddingLeft: "65px",
                  backgroundColor: "var(--pale_green)",
                  width: "100%",
                  display: "inline-flex",
                  marginTop: "auto",
                  marginBottom: "auto",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    width: "100%",
                    marginBottom: "0",
                  }}
                >
                  <ResponsiveHeader
                    as="div"
                    className="margin-top-0 text-bold margin-right-1"
                    style={{
                      display: "inline-flex",
                      color: "#1A103F",
                      fontWeight: "700",
                      fontFamily: "Comfortaa",
                      fontSize: "32px",
                      letterSpacing: "-3%",
                      lineHeight: "60px",
                    }}
                  >
                    {t("searchPageHeading", {
                      count: apiResponse.totalCount || 0,
                      zip: showZipInput
                        ? ""
                        : searchFilters.distanceSearchZipCode,
                    })}
                    {showZipInput && <Ellipses>...</Ellipses>}
                  </ResponsiveHeader>
                  {!isMobile &&
                  <Grid
                    row
                    className="margin-left-4"
                    style={{
                      display: "inline-flex",
                      marginBottom: "auto",
                      marginTop: "auto",
                    }}
                  >
                    <div
                      className="width-full"
                      style={{ marginBottom: "auto", marginTop: "auto" }}
                    >
                      <div
                        className="position-relative inline-flex flex-align-baseline flex-justify flex-column-reverse flex-md-row"
                        style={{ marginBottom: "0" }}
                      >
                        <div
                          className=" inline-flex flex-align-baseline flex-wrap"
                          style={{ marginBottom: "0" }}
                        >
                          {showZipInput && (
                            <SearchContainer>
                              <ZipSearch
                                as="form"
                                onSubmit={() => {
                                  setSearchParams({
                                    ...searchFilters,
                                    distanceSearchZipCode,
                                    page: "1"
                                  });
                                  setShowZipInput(false);
                                }}
                                className="tablet:margin-left-1 display-flex"
                              >
                                <ZipInput
                                  zip={zip ?? ""}
                                  setZip={(_zip) => setZip(_zip)}
                                  setDistanceSearchZipCode={(_zip) =>
                                    setDistanceSearchZipCode(_zip)
                                  }
                                  className="width-full"
                                  noLabel
                                />

                                <Button
                                  className="margin-left-1"
                                  style={{ marginBottom: "0" }}
                                  type="submit"
                                >
                                  {t("search")}
                                </Button>
                              </ZipSearch>
                            </SearchContainer>
                          )}
                          <Button
                            className="padding-y-05 width-auto"
                            type="button"
                            unstyled
                            style={{
                              color: "#1E6692",
                              fontFamily: "Trebuchet MS",
                            }}
                            onClick={() => {
                              setShowZipInput(!showZipInput);
                              setDistanceSearchZipCode(
                                searchFilters.distanceSearchZipCode ?? ""
                              );
                            }}
                          >
                            {showZipInput ? (
                              <>
                                {t("cancel")}{" "}
                                <Close className="margin-left-05" />
                              </>
                            ) : (
                              t("change")
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  }
                </div>
                {!isMobile &&
                <div
                  style={{
                    display: "inline-flex",
                    width: "100%",
                    float: "right",
                    justifyContent: "flex-end",
                    marginBottom: "auto",
                    marginTop: "auto",
                  }}
                >
                  <FuncButtonsOuter>
                    <div
                      style={{
                        gridRow: "auto",
                        display: "inline-flex-block",
                        justifyContent: "flex-end",
                        paddingRight: "5px",
                        float: "right",
                        marginTop: "auto",
                        marginBottom: "auto",
                        width: "auto",
                      }}
                    >
                      <div style={{ display: "inline-flex" }}>
                        <ShareButton text={t("searchPageShare")} />
                      </div>
                      <div
                        style={{
                          display: "inline-flex",
                          justifyContent: "flex-end",
                          paddingRight: "1.5em",
                          paddingLeft:".4em",
                          marginTop: "auto",
                          marginBottom: "auto",
                          fontWeight:700,
                        }}
                      >
                        <PrintButton  />
                      </div>
                    </div>
                  </FuncButtonsOuter>
                </div>
                }
              </div>
              <FilterButtonOuter>
                <FilterButtonInner>
                  <div>
                    <DesktopControl
                      distanceUpdatedExternally={distanceUpdated}
                      filters={{ ...searchFilters }}
                      setFilters={(filters) => {
                        setSearchParams({ ...filters });
                        logEvent(AnalyticsAction.ApplyFilter, {
                          label: "Filter dropdown button",
                        });
                      }}
                    />
                  </div>
                </FilterButtonInner>
              </FilterButtonOuter>
              <MobileControl
                filters={searchFilters}
                setFilters={(filters: any) => setSearchParams({ ...filters })}
                totalResultsCount={apiResponse?.data?.length || 0}
              />
            </div>

            {apiResponse?.data?.length ? (
              <div style={{ borderTop: "1px solid var(--lime_green)" }}>
                <DesktopResults
                  clinicPaginationResponse={apiResponse}
                  filters={searchFilters}
                  setFilters={(filters) => setSearchParams({ ...filters })}
                />
                <MobileResults
                  clinicPaginationResponse={apiResponse}
                  filters={searchFilters}
                  setFilters={(filters) => setSearchParams({ ...filters })}
                />
                <ResultsPagination clinicPaginationResponse={apiResponse} />
              </div>
            ) : (
              <div className="p-5">
                {
                  <div className="no-results-div">
                    <img src={NoResultsLogo} alt="No Results" />
                    <p>
                      {isWidestRadius(searchFilters.distanceSearchRadiusInMiles ?? "25") ? 
                      t("noResultsNoProviders100") :
                      t("noResultsNoProviders")}
                    </p>
                    <p className="lightGrey">{t("noResultsClearFilters")}</p>
                    <Button
                      type="button"
                      style={{ marginBottom: "0" }}
                      onClick={() =>
                        expandSearchRadius(
                          searchFilters.distanceSearchRadiusInMiles ?? "5"
                        )
                      }
                    >
                      {isWidestRadius(searchFilters.distanceSearchRadiusInMiles ?? "25") ?
                      t("noResultsClearFiltersButton100") :
                      t("noResultsClearFiltersButton")}
                    </Button>
                  </div>
                }
              </div>
            )}

            <CompareSelector zip={zip} />
          </PaginationContext.Provider>
        </CompareContext.Provider>

        <InclusionCard
          header={t("requestToAddHeading")}
          cta={t("requestToAddCta")}
          url={t("requestToAddLink")}
        />
      </div>
    </div>
  );
};

export default Search;
